import React, { useState, useEffect } from 'react';
import './ScreenSaver.css';

const sizePool = [1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 4];
const getRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

const ScreenSaver = ({ username, onClose }) => {
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [renderGrid, setRenderGrid] = useState([]);
    const [uiVisible, setUiVisible] = useState(true);

    useEffect(() => {
        if (!username) {
            setError("No username provided.");
            setLoading(false);
            return;
        }

        const limit = 200;
        const apiKey = 'fcdd229d722f1657cd7fedb044df92d2';
        const apiUrl = `https://ws.audioscrobbler.com/2.0/?method=user.gettopalbums&user=${username}&api_key=${apiKey}&format=json&limit=${limit}`;

        const fetchAlbums = async () => {
            try {
                const response = await fetch(apiUrl);
                if (!response.ok) throw new Error(`API returned ${response.status}`);
                const data = await response.json();
                const fetchedAlbums = data?.topalbums?.album;

                if (!fetchedAlbums || fetchedAlbums.length === 0) {
                    setError('No albums found for this user.');
                    setLoading(false);
                    return;
                }
                
                // Keep only valid albums
                const valid = fetchedAlbums.filter(album => {
                   const imgUrl = album.image[3]?.['#text'] || album.image[2]?.['#text'] || album.image[1]?.['#text'];
                   return imgUrl && imgUrl.trim() !== '';
                });

                if (valid.length === 0) {
                    setError('No album art available to display.');
                    setLoading(false);
                    return;
                }

                setAlbums(valid);
                setTimeout(() => setLoading(false), 900);
            } catch (err) {
                console.error(err);
                setError('Failed to load albums. Check your API key or username.');
                setLoading(false);
            }
        };

        fetchAlbums();
    }, [username]);

    useEffect(() => {
        if (albums.length > 0) {
            const targetLength = 1500;
            let randomMapped = [];
            
            while (randomMapped.length < targetLength) {
                // Shuffle albums to ensure no duplicates within the same batch
                const shuffled = [...albums];
                for (let i = shuffled.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                }
                
                const batch = shuffled.map(album => {
                    const imgUrl = album.image[3]?.['#text'] || album.image[2]?.['#text'] || album.image[1]?.['#text'];
                    return { ...album, size: getRandom(sizePool), imgUrl };
                });
                
                randomMapped = randomMapped.concat(batch);
            }
            
            // Trim to exact length
            randomMapped = randomMapped.slice(0, targetLength);

            // Double the array for seamless endless scroll loop
            setRenderGrid([...randomMapped, ...randomMapped]);
        }
    }, [albums]);

    // Close on escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        }
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // Handle mouse movement for hiding UI (close button)
    useEffect(() => {
        let timeout;
        const handleMouseMove = () => {
            setUiVisible(true);
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                setUiVisible(false);
            }, 2000); // Hide after 2 seconds of idleness
        };

        window.addEventListener('mousemove', handleMouseMove);
        
        timeout = setTimeout(() => setUiVisible(false), 2000);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            clearTimeout(timeout);
        };
    }, []);

    return (
        <div className="screensaver-overlay">
            <button className={`close-btn ${uiVisible ? '' : 'hidden'}`} onClick={onClose}>esc to close</button>

            {loading && (
                <div id="loading-screen" className={albums.length > 0 && !loading ? "fade-out" : ""}>
                    <div className="spinner"></div>
                    <p>Loading your library…</p>
                </div>
            )}

            {error && (
                <div id="error-state" style={{ display: 'flex' }}>
                    <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" 
                              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
                    </svg>
                    <span>{error}</span>
                </div>
            )}

            {!loading && !error && renderGrid.length > 0 && (
                <div id="wrapper">
                    <div id="grid-container">
                        {renderGrid.map((item, idx) => (
                            <div key={`${item.name}-${idx}`} className={`grid-item size-${item.size}`}>
                                <img src={item.imgUrl} alt={item.name} loading="lazy" decoding="async" />
                                {item.size > 1 && (
                                    <div className="tooltip">{item.name}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScreenSaver;
